import { useEffect, useRef, useState, useCallback } from "react";

// Ported from frontend_Admin/src/components/common/ImageCropModal.jsx —
// identical canvas-based square/circular-preview crop (drag to pan,
// wheel/pinch to zoom, always covers the frame so the output is always a
// full square, never a gap), restyled to this app's neutral palette.
// Used after BOTH Upload and Take Photo, same as Admin — a raw upload or
// camera frame is essentially never already square, so skipping this step
// would mean object-fit:cover blindly cutting whatever happens to be
// centered. The backend's process_avatar_photo() also center-crops/
// downscales as a defensive second pass — this just hands off an
// already-square crop so nothing important gets clipped by surprise.
const VIEWPORT = 288;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const OUTPUT_SIZE = 320; // server downscales to 256 anyway; a little headroom before that final resize

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export default function ImageCropModal({ imageSrc, onConfirm, onCancel }) {
  const [image, setImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const centeredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    centeredRef.current = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setImage(img);
    };
    img.src = imageSrc;
    return () => {
      cancelled = true;
    };
  }, [imageSrc]);

  const baseScale = image ? Math.max(VIEWPORT / image.naturalWidth, VIEWPORT / image.naturalHeight) : 1;
  const scale = baseScale * zoom;
  const dispW = image ? image.naturalWidth * scale : VIEWPORT;
  const dispH = image ? image.naturalHeight * scale : VIEWPORT;

  const clampOffset = useCallback(
    (x, y, w, h) => ({
      x: clamp(x, VIEWPORT - w, 0),
      y: clamp(y, VIEWPORT - h, 0),
    }),
    []
  );

  useEffect(() => {
    if (!image) return;
    if (!centeredRef.current) {
      centeredRef.current = true;
      setOffset(clampOffset((VIEWPORT - dispW) / 2, (VIEWPORT - dispH) / 2, dispW, dispH));
    } else {
      setOffset((prev) => clampOffset(prev.x, prev.y, dispW, dispH));
    }
    // eslint-disable-next-line
  }, [image, zoom]);

  function onPointerDown(e) {
    if (!image) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startOffsetX: offset.x, startOffsetY: offset.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e) {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clampOffset(dragRef.current.startOffsetX + dx, dragRef.current.startOffsetY + dy, dispW, dispH));
  }
  function onPointerUp() {
    dragRef.current = null;
  }

  function onWheel(e) {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setZoom((z) => clamp(z + delta, MIN_ZOOM, MAX_ZOOM));
  }

  function touchDist(touches) {
    const [a, b] = touches;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }
  function onTouchStart(e) {
    if (e.touches.length === 2) {
      pinchRef.current = { startDist: touchDist(e.touches), startZoom: zoom };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      dragRef.current = { startX: t.clientX, startY: t.clientY, startOffsetX: offset.x, startOffsetY: offset.y };
    }
  }
  function onTouchMove(e) {
    e.preventDefault();
    if (e.touches.length === 2 && pinchRef.current) {
      const ratio = touchDist(e.touches) / pinchRef.current.startDist;
      setZoom(clamp(pinchRef.current.startZoom * ratio, MIN_ZOOM, MAX_ZOOM));
    } else if (e.touches.length === 1 && dragRef.current) {
      const t = e.touches[0];
      const dx = t.clientX - dragRef.current.startX;
      const dy = t.clientY - dragRef.current.startY;
      setOffset(clampOffset(dragRef.current.startOffsetX + dx, dragRef.current.startOffsetY + dy, dispW, dispH));
    }
  }
  function onTouchEnd() {
    dragRef.current = null;
    pinchRef.current = null;
  }

  function confirm() {
    if (!image) return;
    const sx = -offset.x / scale;
    const sy = -offset.y / scale;
    const sSize = VIEWPORT / scale;
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    onConfirm(canvas.toDataURL("image/jpeg", 0.92));
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h3 className="font-semibold text-neutral-900">Position Photo</h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel crop"
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-col items-center gap-4 p-5">
          <div
            className="relative select-none overflow-hidden rounded-lg bg-neutral-900 touch-none"
            style={{ width: VIEWPORT, height: VIEWPORT, cursor: image ? "grab" : "default" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onWheel={onWheel}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {image && (
              <img
                src={imageSrc}
                alt="Crop preview"
                draggable={false}
                className="absolute pointer-events-none"
                style={{ left: offset.x, top: offset.y, width: dispW, height: dispH, maxWidth: "none", maxHeight: "none" }}
              />
            )}
            <div
              className="pointer-events-none absolute rounded-full"
              style={{
                left: 16,
                top: 16,
                right: 16,
                bottom: 16,
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
                border: "2px solid rgba(255,255,255,0.9)",
              }}
            />
          </div>

          <div className="flex w-full items-center gap-3">
            <span className="text-xs text-neutral-400">−</span>
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(clamp(Number(e.target.value), MIN_ZOOM, MAX_ZOOM))}
              className="flex-1 accent-neutral-900"
              aria-label="Zoom"
            />
            <span className="text-xs text-neutral-400">+</span>
          </div>
          <p className="text-center text-[11px] text-neutral-400">Drag to reposition · scroll or pinch to zoom</p>
        </div>

        <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!image}
            className="rounded-lg bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-neutral-800 disabled:opacity-50"
          >
            Use Photo
          </button>
        </div>
      </div>
    </div>
  );
}
